/**
 * Realtime synchronization client using Server-Sent Events (SSE).
 * Connects to /api/realtime/events and automatically dispatches incoming
 * mutation events to registered entity listeners.
 *
 * Features:
 * - Exponential backoff reconnection on network drop or server restart.
 * - Automatic reconnection on window 'online' event.
 * - Automatic re-sync when tab becomes active again ('visibilitychange').
 * - Entity-based event dispatching ('banners', 'products', 'categories', etc.).
 */

import { API_URL } from '../config/api';

class RealtimeClient {
    constructor() {
        this.eventSource = null;
        this.listeners = new Map(); // entity (string) -> Set<function>
        this.attachedEvents = new Set(); // SSE custom event names attached to EventSource
        this.retryCount = 0;
        this.baseDelay = 1000; // 1s initial delay
        this.maxDelay = 30000; // 30s maximum delay
        this.reconnectTimer = null;
        this.isConnected = false;

        this.setupLifecycleListeners();
    }

    /**
     * Compute the full SSE events endpoint URL.
     * Uses the same API_URL as the rest of the app (from config/api.js → REACT_APP_API_URL).
     * API_URL is already normalized (e.g. "http://localhost:5000/api").
     * We simply append "/realtime/events" to it.
     */
    getEventsUrl() {
        // API_URL = "http://localhost:5000/api" (or "/api" in production with proxy)
        const base = (API_URL || '/api').replace(/\/+$/, '');
        return `${base}/realtime/events`;
    }

    /**
     * Set up window 'online' and 'visibilitychange' listeners to auto-reconnect.
     */
    setupLifecycleListeners() {
        if (typeof window === 'undefined') return;

        // When user regains network connection
        window.addEventListener('online', () => {
            this.retryCount = 0;
            this.reconnectImmediate();
        });

        // When user switches back to active browser tab
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                if (!this.eventSource || this.eventSource.readyState === EventSource.CLOSED) {
                    this.retryCount = 0;
                    this.reconnectImmediate();
                }
            }
        });
    }

    /**
     * Connect to the SSE endpoint if not already connected.
     */
    connect() {
        if (typeof window === 'undefined' || typeof EventSource === 'undefined') {
            return;
        }

        // Avoid duplicate connections
        if (
            this.eventSource &&
            (this.eventSource.readyState === EventSource.OPEN ||
             this.eventSource.readyState === EventSource.CONNECTING)
        ) {
            return;
        }

        this.cleanup();

        const url = this.getEventsUrl();

        try {
            this.eventSource = new EventSource(url);

            this.eventSource.onopen = () => {
                this.isConnected = true;
                this.retryCount = 0;
                if (this.reconnectTimer) {
                    clearTimeout(this.reconnectTimer);
                    this.reconnectTimer = null;
                }
            };

            // Standard SSE message handler
            this.eventSource.onmessage = (event) => {
                this.handleRawMessage(event);
            };

            // Error handling with exponential backoff
            this.eventSource.onerror = (error) => {
                this.cleanup();
                this.scheduleReconnect();
            };

            // Re-attach any custom SSE listeners for registered entities
            this.listeners.forEach((_, entity) => {
                this.attachEntityEventListener(entity);
            });
        } catch (err) {
            this.cleanup();
            this.scheduleReconnect();
        }
    }

    /**
     * Handle incoming raw message event
     */
    handleRawMessage(event) {
        if (!event || !event.data) return;

        let payload;
        try {
            payload = JSON.parse(event.data);
        } catch {
            payload = event.data;
        }

        // Heartbeat or ping event
        if (payload === 'ping' || payload?.type === 'ping' || payload?.type === 'connected') {
            return;
        }

        // Extract entity name from payload if present
        const entity = (
            payload?.entity ||
            payload?.type ||
            payload?.resource ||
            payload?.table ||
            '*'
        );

        this.dispatch(entity, payload);
    }

    /**
     * Attach custom SSE event listener on EventSource (for servers using `event: <entity>`)
     */
    attachEntityEventListener(entity) {
        if (!this.eventSource || !entity || entity === '*' || this.attachedEvents.has(entity)) {
            return;
        }

        const normalized = String(entity).trim().toLowerCase();
        this.attachedEvents.add(normalized);

        this.eventSource.addEventListener(normalized, (event) => {
            let payload;
            try {
                payload = JSON.parse(event.data);
            } catch {
                payload = event.data;
            }
            this.dispatch(normalized, payload);
        });
    }

    /**
     * Schedule a reconnection attempt with exponential backoff.
     */
    scheduleReconnect() {
        if (this.reconnectTimer || typeof window === 'undefined') return;

        // Exponential backoff: baseDelay * 1.5^retryCount + random jitter
        const delay = Math.min(
            this.baseDelay * Math.pow(1.5, this.retryCount) + Math.random() * 500,
            this.maxDelay
        );
        this.retryCount++;

        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            // Only reconnect if there are active listeners
            if (this.listeners.size > 0) {
                this.connect();
            }
        }, delay);
    }

    /**
     * Immediate reconnect (e.g. after network online or tab focus).
     */
    reconnectImmediate() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        this.cleanup();
        if (this.listeners.size > 0) {
            this.connect();
        }
    }

    /**
     * Clean up current EventSource instance and state.
     */
    cleanup() {
        if (this.eventSource) {
            this.eventSource.onopen = null;
            this.eventSource.onerror = null;
            this.eventSource.onmessage = null;
            try {
                this.eventSource.close();
            } catch {}
            this.eventSource = null;
        }
        this.isConnected = false;
        this.attachedEvents.clear();
    }

    /**
     * Subscribe a callback to real-time events for a specific entity.
     * @param {string} entity - Entity identifier (e.g. 'banners', 'products', 'categories', '*')
     * @param {function} onMutation - Callback triggered when a mutation occurs
     * @returns {function} Unsubscribe function
     */
    subscribe(entity, onMutation) {
        if (typeof onMutation !== 'function') {
            return () => {};
        }

        const normalized = String(entity || '*').trim().toLowerCase();

        if (!this.listeners.has(normalized)) {
            this.listeners.set(normalized, new Set());
        }

        this.listeners.get(normalized).add(onMutation);

        // Ensure EventSource is connected
        this.connect();

        // Attach SSE event listener for this entity if custom SSE event names are used
        this.attachEntityEventListener(normalized);

        // Return unsubscribe function
        return () => {
            const set = this.listeners.get(normalized);
            if (set) {
                set.delete(onMutation);
                if (set.size === 0) {
                    this.listeners.delete(normalized);
                }
            }
        };
    }

    /**
     * Dispatch an event to all matching listeners (entity-specific and wildcard).
     * @param {string} entity - Entity name
     * @param {any} payload - Event data
     */
    dispatch(entity, payload) {
        const normalized = String(entity || '').trim().toLowerCase();

        // 1. Dispatch to entity-specific listeners
        const specificListeners = this.listeners.get(normalized);
        if (specificListeners && specificListeners.size > 0) {
            specificListeners.forEach((callback) => {
                try {
                    callback(payload);
                } catch (err) {
                    console.error(`[realtimeClient] Error in listener for "${normalized}":`, err);
                }
            });
        }

        // 2. Dispatch to wildcard listeners
        if (normalized !== '*') {
            const wildcardListeners = this.listeners.get('*');
            if (wildcardListeners && wildcardListeners.size > 0) {
                wildcardListeners.forEach((callback) => {
                    try {
                        callback(payload);
                    } catch (err) {
                        console.error('[realtimeClient] Error in wildcard listener:', err);
                    }
                });
            }
        }
    }

    /**
     * Disconnect entirely and clear all timers.
     */
    disconnect() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        this.cleanup();
    }
}

const realtimeClient = new RealtimeClient();
export default realtimeClient;
